class AtlasFeatureSplit extends HTMLElement {
  constructor() {
    super();
    this.panels = [];
    this.row = null;
    this.activePanel = null;
    this.mqExpand = window.matchMedia('(min-width: 768px)');
    this.mqMobile = window.matchMedia('(max-width: 767px)');
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  }

  connectedCallback() {
    this.row = this.querySelector('[data-afs-row]');
    this.panels = Array.from(this.querySelectorAll('[data-afs-panel]'));
    this.panels.forEach((panel) => this.preparePanel(panel));

    if (this.row) {
      // Event delegation: listeners live on the row, the target panel is
      // resolved from event.target.
      this.row.addEventListener('mouseover', this.onPointerOver);
      this.row.addEventListener('mouseleave', this.onPointerLeave);
      this.row.addEventListener('focusin', this.onFocusIn);
      this.row.addEventListener('focusout', this.onFocusOut);
    }

    this.handleMobileAutoplay();
    this._onMqChange = () => this.handleMobileAutoplay();
    if (this.mqMobile.addEventListener) {
      this.mqMobile.addEventListener('change', this._onMqChange);
    }
  }

  disconnectedCallback() {
    if (this.row) {
      this.row.removeEventListener('mouseover', this.onPointerOver);
      this.row.removeEventListener('mouseleave', this.onPointerLeave);
      this.row.removeEventListener('focusin', this.onFocusIn);
      this.row.removeEventListener('focusout', this.onFocusOut);
    }
    if (this.mqMobile.removeEventListener && this._onMqChange) {
      this.mqMobile.removeEventListener('change', this._onMqChange);
    }
  }

  preparePanel(panel) {
    const video = panel.querySelector('.afs__video');
    const progressFill = panel.querySelector('[data-afs-progress]');
    panel._afsVideo = video;
    panel._afsProgress = progressFill;

    if (video) {
      video.addEventListener('timeupdate', () => {
        if (!progressFill || !video.duration || Number.isNaN(video.duration)) return;
        const ratio = Math.min(video.currentTime / video.duration, 1);
        progressFill.style.transform = `scaleX(${ratio})`;
      });
    }
  }

  /* ---- delegated handlers ---- */
  onPointerOver = (event) => {
    if (!this.mqExpand.matches) return;
    const panel = event.target.closest('[data-afs-panel]');
    if (!panel || !this.row.contains(panel)) return;
    this.activate(panel);
  };

  onPointerLeave = () => {
    this.deactivateAll();
  };

  onFocusIn = (event) => {
    if (!this.mqExpand.matches) return;
    const panel = event.target.closest('[data-afs-panel]');
    if (!panel || !this.row.contains(panel)) return;
    this.activate(panel);
  };

  onFocusOut = (event) => {
    const next = event.relatedTarget;
    if (next && next.closest && next.closest('[data-afs-panel]')) return;
    this.deactivateAll();
  };

  /* ---- state ---- */
  activate(panel) {
    if (this.activePanel === panel) return;
    if (this.activePanel) this.deactivatePanel(this.activePanel);

    this.activePanel = panel;
    if (this.row) this.row.classList.add('is-hovering');
    panel.classList.add('is-active');
    this.startVideo(panel);
  }

  deactivateAll() {
    if (this.activePanel) this.deactivatePanel(this.activePanel);
    this.activePanel = null;
    if (this.row) this.row.classList.remove('is-hovering');
  }

  deactivatePanel(panel) {
    panel.classList.remove('is-active');
    this.stopVideo(panel);
  }

  /* ---- video ---- */
  startVideo(panel) {
    const video = panel._afsVideo;
    if (!video || this.prefersReducedMotion.matches) return;
    panel.classList.add('is-playing');
    try {
      video.currentTime = 0;
    } catch (e) {
      /* metadata may not be ready yet */
    }
    if (panel._afsProgress) panel._afsProgress.style.transform = 'scaleX(0)';
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  }

  stopVideo(panel) {
    panel.classList.remove('is-playing');
    const video = panel._afsVideo;
    if (!video) return;
    video.pause();
    try {
      video.currentTime = 0;
    } catch (e) {
      /* noop */
    }
    if (panel._afsProgress) panel._afsProgress.style.transform = 'scaleX(0)';
  }

  handleMobileAutoplay() {
    const autoplay = this.getAttribute('data-mobile-autoplay') === 'true';
    const isMobile = this.mqMobile.matches;

    this.panels.forEach((panel) => {
      const video = panel._afsVideo;
      if (!video) return;

      if (isMobile && autoplay && !this.prefersReducedMotion.matches) {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      } else if (isMobile) {
        this.stopVideo(panel);
      }
    });
  }
}

if (!window.customElements.get('atlas-feature-split')) {
  window.customElements.define('atlas-feature-split', AtlasFeatureSplit);
}
